import React, { useState, useEffect, useCallback } from 'react';
import '../App.css';
import {Notification} from "./Notification";

const API_URL = window.location.origin;

export function ProfilePage({ userId, onPageLoaded, onLogout }) {
    const [email, setEmail] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [lastEmailChange, setLastEmailChange] = useState(null);
    const [canChangeEmail, setCanChangeEmail] = useState(true);
    const [timeRemaining, setTimeRemaining] = useState(null);

    // Состояния для модального окна подтверждения
    const [showCodeModal, setShowCodeModal] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [tempNewEmail, setTempNewEmail] = useState('');
    const [codeLoading, setCodeLoading] = useState(false);
    const [notification, setNotification] = useState({isVisible: false, message: '', type: 'success'});

    useEffect(() => {
        fetchUserData();
        if (onPageLoaded) onPageLoaded();
    }, [userId]);

    useEffect(() => {
        if (!canChangeEmail && lastEmailChange) {
            const interval = setInterval(() => {
                const now = new Date();
                const nextAvailable = new Date(lastEmailChange);
                nextAvailable.setHours(nextAvailable.getHours() + 24);

                const diff = nextAvailable - now;
                if (diff <= 0) {
                    setCanChangeEmail(true);
                    setTimeRemaining(null);
                    clearInterval(interval);
                } else {
                    const hours = Math.floor(diff / (1000 * 60 * 60));
                    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                    setTimeRemaining(`${hours}ч ${minutes}м`);
                }
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [canChangeEmail, lastEmailChange]);

    const fetchUserData = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/user/${userId}/profile`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();

            if (data.success) {
                setEmail(data.email);
                setLastEmailChange(data.last_email_change);
                setCanChangeEmail(data.can_change_email);
                if (data.hours_left) {
                    setTimeRemaining(`${data.hours_left}ч`);
                }
            }
        } catch (error) {
            console.error('Ошибка загрузки профиля:', error);
            setError('Не удалось загрузить данные профиля');
        }
    };

    // Шаг 1: Запрос на смену email (отправка кода)
    const handleRequestEmailChange = async () => {
        if (!newEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            setError('Введите корректный email');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/auth/request-email-change`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ userId, newEmail })
            });

            const data = await response.json();

            if (data.success) {
                setTempNewEmail(newEmail);
                setVerificationCode(''); // Очищаем код
                setShowCodeModal(true);
                setSuccess(null);
                setError(null);
            } else {
                setError(data.error || 'Ошибка при запросе смены email');
            }
        } catch (error) {
            console.error('Ошибка:', error);
            setError('Ошибка соединения с сервером');
        } finally {
            setLoading(false);
        }
    };

    // Шаг 2: Подтверждение кода и смена email
    const handleConfirmEmailChange = async () => {
        if (!verificationCode || verificationCode.length !== 6) {
            setError('Введите 6-значный код подтверждения');
            return;
        }

        setCodeLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/auth/confirm-email-change`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    userId,
                    code: verificationCode
                })
            });

            const data = await response.json();

            if (data.success) {
                // ✅ 1. Обновляем email в состоянии
                setEmail(tempNewEmail);

                // ✅ 2. Обновляем токен в localStorage
                if (data.token) {
                    localStorage.setItem('token', data.token);
                }

                // ✅ 3. Обновляем данные пользователя в localStorage
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                user.email = tempNewEmail;
                user.emailVerified = false;
                localStorage.setItem('user', JSON.stringify(user));

                // ✅ 4. Закрываем модальное окно
                setShowCodeModal(false);

                // ✅ 5. Очищаем поля ввода
                setVerificationCode('');
                setNewEmail('');
                setIsEditing(false);

                // ✅ 6. Показываем сообщение об успехе
                setSuccess('Email успешно изменен! Письмо с подтверждением отправлено на новый email.');

                // ✅ 7. Обновляем время последней смены (чтобы заблокировать повторную смену на 24 часа)
                setLastEmailChange(new Date().toISOString());
                setCanChangeEmail(false);
                setTimeRemaining('24ч');
                await refreshUserData(); // Обновляем данные
                // ✅ 8. Через 3 секунды разлогиниваем пользователя
                setTimeout(() => {
                    showNotification('Пожалуйста, подтвердите новый email перейдя по ссылке в письме. После подтверждения войдите снова.', 'success');
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    window.location.href = '/';
                }, 3000);

            } else {
                setError(data.error || 'Ошибка при подтверждении кода');
            }
        } catch (error) {
            console.error('Ошибка:', error);
            setError('Ошибка соединения с сервером');
        } finally {
            setCodeLoading(false);
        }
    };

    const showNotification = (message, type = 'success') => {
        setNotification({isVisible: true, message, type});
        setTimeout(() => setNotification({isVisible: false, message: '', type: 'success'}), 3000);
    };

    const refreshUserData = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/user/${userId}/profile`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();

            if (data.success) {
                setEmail(data.email);
                setLastEmailChange(data.last_email_change);
                setCanChangeEmail(data.can_change_email);
                if (data.hours_left) {
                    setTimeRemaining(`${data.hours_left}ч`);
                }
            }
        } catch (error) {
            console.error('Ошибка обновления профиля:', error);
        }
    }, [userId]);

    const handleCancelEdit = () => {
        setIsEditing(false);
        setNewEmail('');
        setError(null);
        setSuccess(null);
    };

    const handleCodeChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setVerificationCode(value);
    };

    const handleCloseModal = () => {
        setShowCodeModal(false);
        setVerificationCode('');
        setError(null);
    };

    // ✅ ВАЖНО: Модальное окно создается один раз и не пересоздается при каждом рендере
    const renderCodeVerificationModal = () => {
        if (!showCodeModal) return null;

        return (
            <div className="modal-overlay" onClick={handleCloseModal}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Подтверждение смены email</h2>
                        <button className="modal-close" onClick={handleCloseModal}>×</button>
                    </div>
                    <div className="modal-body">
                        <div className="verify-icon">🔐</div>
                        <p>На ваш текущий email отправлен код подтверждения:</p>
                        <p className="verify-email">{email}</p>
                        <div className="code-input-group">
                            <input
                                type="text"
                                maxLength="6"
                                className="code-input-full"
                                placeholder="Введите 6-значный код"
                                value={verificationCode}
                                onChange={handleCodeChange}
                                autoFocus
                            />
                        </div>
                        <p className="verify-text-small">
                            Код действителен 15 минут. Не закрывайте это окно.
                        </p>
                        <div className="verify-actions">
                            <button
                                className="resend-verify-btn"
                                onClick={handleRequestEmailChange}
                                disabled={loading}
                            >
                                {loading ? 'Отправка...' : 'Отправить код повторно'}
                            </button>
                            <button
                                className="confirm-verify-btn"
                                onClick={handleConfirmEmailChange}
                                disabled={codeLoading}
                            >
                                {codeLoading ? 'Проверка...' : 'Подтвердить и сменить email'}
                            </button>
                        </div>
                        {error && <div className="verify-error">{error}</div>}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="profile-page">
            <div className="profile-container">
                <header className="page-header">
                    <h1>Личный кабинет</h1>
                </header>

                <div className="profile-card">
                    <div className="profile-info">
                        <div className="info-row">
                            <span className="info-label">Внутренний ID:</span>
                            <span className="info-value">{userId}</span>
                        </div>

                        <div className="info-row">
                            <span className="info-label">Email:</span>
                            <div className="email-section">
                                {!isEditing ? (
                                    <>
                                        <span className="info-value">{email || 'Не указан'}</span>
                                        {canChangeEmail && (
                                            <button
                                                className="change-email-btn"
                                                onClick={() => setIsEditing(true)}
                                            >
                                                Изменить
                                            </button>
                                        )}
                                        {!canChangeEmail && lastEmailChange && (
                                            <span className="change-cooldown">
                                                Доступно через {timeRemaining}
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    <div className="email-edit-form">
                                        <input
                                            type="email"
                                            className="email-input"
                                            placeholder="Новый email"
                                            value={newEmail}
                                            onChange={(e) => setNewEmail(e.target.value)}
                                            autoFocus
                                        />
                                        <div className="edit-actions">
                                            <button
                                                className="save-btn"
                                                onClick={handleRequestEmailChange}
                                                disabled={loading}
                                            >
                                                {loading ? 'Отправка кода...' : 'Отправить код'}
                                            </button>
                                            <button
                                                className="cancel-btn"
                                                onClick={handleCancelEdit}
                                            >
                                                Отмена
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {error && !showCodeModal && <div className="profile-error">{error}</div>}
                    {success && <div className="profile-success">{success}</div>}
                </div>

                {/*<div className="profile-actions">*/}
                {/*    <button className="logout-button-full" onClick={onLogout}>*/}
                {/*        Выйти из аккаунта*/}
                {/*    </button>*/}
                {/*</div>*/}
            </div>
            <Notification message={notification.message} type={notification.type} isVisible={notification.isVisible}
                          onClose={() => setNotification({...notification, isVisible: false})} duration={3000}/>
            {/* ✅ Модальное окно рендерится отдельно */}
            {renderCodeVerificationModal()}
        </div>
    );
}
